import React from 'react'
import { observer, inject } from 'mobx-react'
import get from 'lodash/get'

import browserHistory from '@client/utils/browser-history'
import { captureException } from '@client/utils/sentry'
import { APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN } from '@config/app.config'

import './styles.scss'
import {
  WIZARD_TYPE_FORM,
  WIZARD_TYPE_PAGE,
  WIZARD_TYPE_SURVEY,
} from '../Editor/settings'

const BREADCRUMBS = {
  [WIZARD_TYPE_FORM]: [
    {
      title: 'jsforms.footer.form',
      link: `/forms/:hash/form`,
    },
    {
      title: 'jsforms.footer.form-success',
      link: `/forms/:hash/form-success`,
    },
    {
      title: 'jsforms.footer.mail',
      link: `/forms/:hash/mail`,
    },
    {
      title: 'jsforms.footer.page',
      link: `/forms/:hash/page`,
    },
  ],
  [WIZARD_TYPE_PAGE]: [
    {
      title: 'landing-pages.footer.form',
      link: `/pages/:hash/landing-page`,
    },
    {
      title: 'landing-pages.footer.form-success',
      link: `/pages/:hash/landing-page-success`,
    },
    {
      title: 'landing-pages.footer.mail',
      link: `/pages/:hash/mail`,
    },
    {
      title: 'landing-pages.footer.page',
      link: `/pages/:hash/page`,
    },
  ],
  [WIZARD_TYPE_SURVEY]: [
    {
      title: 'surveys.footer.form',
      link: `/surveys/:hash/survey`,
    },
    {
      title: 'surveys.footer.thank-you-page',
      link: `/surveys/:hash/thank-you-page`,
    },
  ],
}

// Prefer the sanitized `next` path from the launching app (frontend-v3), but
// only when it was captured for THIS wizard — both hash AND type must match,
// since hash uniqueness is only guaranteed within a single item type (e.g.
// surveys use a separate, independently-generated hash pool from forms/pages)
// — so a stale value from another wizard can't leak in.
const getReturnOverride = (wizard, returnUrl, returnUrlHash, returnUrlType) =>
  returnUrl && returnUrlHash === wizard.hash && returnUrlType === wizard.type
    ? `${APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN}${returnUrl}`
    : null

export const getNextUrl = (
  wizard,
  editor,
  returnUrl,
  returnUrlHash,
  returnUrlType
) => {
  const returnOverride = getReturnOverride(
    wizard,
    returnUrl,
    returnUrlHash,
    returnUrlType
  )
  if (returnOverride) {
    return returnOverride
  }
  switch (wizard.type) {
    case WIZARD_TYPE_FORM:
      return `${APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN}/forms/${`${editor.form_type}`.replace(
        '_',
        '-'
      )}/${wizard.hash}/settings`
    case WIZARD_TYPE_PAGE: {
      if (editor.status === 'draft') {
        return `${APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN}/pages/${wizard.hash}/publish`
      }
      return `${APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN}/pages/${wizard.hash}`
    }
    case WIZARD_TYPE_SURVEY: {
      if (editor.status === 'draft') {
        return `${APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN}/surveys/${wizard.hash}/publish`
      }
      return `${APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN}/surveys/${wizard.hash}`
    }
    default:
      return ''
  }
}

export function FooterNav({ stepIndex, wizard, editor, app }) {
  const { formatMessage } = app
  const [breadcrumbs, setBreadcrumbs] = React.useState([])

  React.useEffect(() => {
    const breadcrumbs = (BREADCRUMBS[wizard.type] || []).map((item) => ({
      ...item,
      title: formatMessage(item.title),
      link: item.link.replace(':hash', wizard.hash),
    }))
    setBreadcrumbs(breadcrumbs)
  }, [setBreadcrumbs, formatMessage, wizard, app.locale])

  const onClick = React.useCallback(
    async (event) => {
      event.preventDefault() // prevent full page reload
      // read from currentTarget (the <a>, not a child span) before the await —
      // React clears currentTarget once the synchronous part of the handler returns
      const href = event.currentTarget.getAttribute('href')
      try {
        await editor.save()
      } catch (e) {
        captureException(e)
      }
      // The next step loads a different document, so unsaved edits would be
      // lost. A rejected save shows the save error banner, a 5xx or a dropped
      // connection the reconnect dialog (FE-1431), and a newer version the save
      // conflict dialog (FE-1441).
      if (editor.lastSaveFailed || editor.saveConflict) {
        return
      }
      browserHistory.push(href) // do SPA navigation
    },
    [editor]
  )

  if (!breadcrumbs.length) {
    return null
  }

  const type = get(app, 'editingSettings.type')
  const showSettings = !(type === 'NULL')

  return (
    <div
      className="gan-footer-nav bp6-dialog-header gan-tw"
      data-testid="footer-nav"
    >
      <a
        type="button"
        className={`bp6-button previous-form-step gan-footer-nav__prev-next gan-header-responsive-icon-btn bp6-icon-arrow-left whitespace-nowrap ${
          stepIndex === 0 || showSettings ? 'bp6-disabled' : ''
        }`}
        href={breadcrumbs[stepIndex - 1 > 0 ? stepIndex - 1 : 0].link}
        onClick={onClick}
      >
        <span className="hidden xl:block">
          {formatMessage('jsforms.footer.prev')}
        </span>
      </a>
      <ul className="bp6-breadcrumbs center">
        <For each="crumbName" of={breadcrumbs} index="index">
          <li key={index}>
            <If condition={index === stepIndex}>
              <span
                className="bp6-breadcrumb bp6-breadcrumb-current gan-footer-nav__link"
                aria-current="step"
              >
                {breadcrumbs[index].title}
              </span>
            </If>
            <If condition={index !== stepIndex}>
              <a
                className={`bp6-breadcrumb gan-footer-nav__link ${
                  showSettings ? 'bp6-disabled' : ''
                }`}
                href={breadcrumbs[index].link}
                onClick={onClick}
              >
                {breadcrumbs[index].title}
              </a>
            </If>
          </li>
        </For>
      </ul>
      <If condition={stepIndex !== breadcrumbs.length - 1}>
        <a
          type="button"
          className={`bp6-button bp6-intent-success next-form-step gan-footer-nav__prev-next ${
            showSettings ? 'bp6-disabled' : ''
          }`}
          href={breadcrumbs[stepIndex + 1].link}
          onClick={onClick}
        >
          <span className="hidden whitespace-nowrap xl:block">
            {formatMessage('jsforms.footer.next')}
          </span>
          <span className="bp6-icon-standard bp6-icon-arrow-right gan-footer-nav__step-icon" />
        </a>
      </If>
      <If condition={stepIndex === breadcrumbs.length - 1}>
        <a
          type="button"
          className={`bp6-button bp6-intent-success next-form-step gan-footer-nav__prev-next ${
            showSettings ? 'bp6-disabled' : ''
          }`}
          href={getNextUrl(
            wizard,
            editor,
            app.returnUrl,
            app.returnUrlHash,
            app.returnUrlType
          )}
        >
          <span className="hidden whitespace-nowrap xl:block">
            {formatMessage('jsforms.footer.continue')}
          </span>
          <span className="bp6-icon-standard bp6-icon-arrow-right gan-footer-nav__step-icon" />
        </a>
      </If>
    </div>
  )
}

export default inject('editor')(observer(FooterNav))
